import React, { useState, useEffect, useMemo } from "react";
import DataTable from "examples/Tables/DataTable";
import CrudService from "services/cruds-service";
import {Tooltip, IconButton, CircularProgress, Tooltip as MuiTooltip} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Add from "@mui/icons-material/Add";
import { CrmPage, CrmPageHeader, CrmActionButton, CrmTableCard, CrmNotice } from "../shared/CrmUi";
import { useNavigate } from "react-router-dom";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import { appConfirm } from "components/AppDialog";

const sum = (arr) => arr.reduce((a, b) => a + b, 0);
const kmKey = (sid, km, idx) => km.id || km._id || `${sid}-${idx}`;

function TargetTable() {
    const [targets, setTargets] = useState([]);
    const [rows, setRows] = useState([]);
    const [notif, setNotif] = useState({ open: false, text: "" });
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const resp = await CrudService.getTargets();
                const normalized = resp.data.map((t) => ({ id: t.id, ...t.attributes }));
                setTargets(normalized);
            } catch (err) {
                console.error(err);
                setNotif({ open: true, text: "Failed to load targets." });
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    /* ------------ fetch strategies per target ------------ */
    useEffect(() => {
        if (!targets.length) return;

        const buildRows = async () => {
            const result = await Promise.all(
                targets.map(async (target) => {
                    /* --- ana kutu + sadece KM seçili stratejiler --- */
                    const unionIds = [
                        ...(target.strategies || []),
                        ...Object.keys(target.selectedKeyMarkets || {}),
                    ];

                    /* --- stratejileri getir --- */
                    const stratInfos = await Promise.all(
                        unionIds.map(async (sRef) => {
                            if (typeof sRef === "object") return sRef;
                            const res = await CrudService.getStrategyById(sRef);
                            return { id: sRef, ...res.data.attributes };
                        })
                    );

                    /* --- skor fonksiyonu: sadece seçili KM --- */
                    const scoreForStrategy = (st) => {
                        const selKM = (target.selectedKeyMarkets || {})[st.id] || [];
                        if (selKM.length)
                            return sum(
                                st.keyMarkets.map((km, idx) =>
                                    selKM.includes(kmKey(st.id, km, idx)) ? Number(km.score || 0) : 0
                                )
                            );
                        return sum(st.keyMarkets.map((km) => Number(km.score || 0)));
                    };

                    const totalScore = sum(stratInfos.map(scoreForStrategy));

                    const highest = stratInfos.reduce(
                        (prev, cur) => (scoreForStrategy(cur) > scoreForStrategy(prev) ? cur : prev),
                        stratInfos[0] || {}
                    );

                    /* --- sadece seçili KM’leri etiketle --- */
                    const selectedStrategies = stratInfos
                        .map((st) => {
                            const selKM = (target.selectedKeyMarkets || {})[st.id] || [];
                            if (selKM.length) {
                                return st.keyMarkets
                                    .map((km, idx) =>
                                        selKM.includes(kmKey(st.id, km, idx))
                                            ? `${st.strategyName}: ${km.continent ||
                                            km.region ||
                                            (km.countries || []).join(", ")}`
                                            : null
                                    )
                                    .filter(Boolean)
                                    .join("; ");
                            }
                            return st.strategyName;
                        })
                        .filter(Boolean)
                        .join(", ");

                    return {
                        id: target.id,
                        leadCompany: (
                            <MuiTooltip title={target.leadCompany || ""} placement="top" arrow>
                            <span>
                              {target.leadCompany?.length > 40 ? `${target.leadCompany.slice(0, 37)}...` : target.leadCompany || ""}
                            </span>
                            </MuiTooltip>
                        ),
                        leadName: Array.isArray(target.leadRepresentative)
                            ? target.leadRepresentative.map((u) => u?.name || "").join(", ")
                            : (target.leadRepresentative?.name || target.leadRepresentative || "-"),
                        selectedStrategies,
                        totalScore,
                        highestStrategy: highest.strategyName || "-",
                        actions: (
                            <MDBox display="flex" gap="5px">
                                {/* Düzenle */}
                                <Tooltip title="Edit Target">
                                    <IconButton
                                        onClick={() => navigate(`/target/create-lead/details?id=${target._id || target.id}`)
                                        }
                                    >
                                        <EditIcon sx={{ color: "#175CD3" }} fontSize="small" />
                                    </IconButton>
                                </Tooltip>

                                {/* Create Meeting */}
                                <Tooltip title="Create Meeting">
                                    <IconButton
                                        onClick={() =>
                                            navigate(
                                                `/meeting/create-meeting/details`,
                                                {
                                                    state: {
                                                        leadCompany: target.leadCompany,
                                                        leadId: target.id
                                                    }
                                                }
                                            )
                                        }
                                    >
                                        <CalendarMonthIcon sx={{ color: "#1976d2" }} />
                                    </IconButton>
                                </Tooltip>

                                {/* Create Opportunity */}
                                <Tooltip title="Create Opportunity">
                                    <IconButton
                                        onClick={() =>
                                            navigate(
                                                `/opportunity/create-opportunity/details`,
                                                {
                                                    state: {
                                                        customer: target.leadCompany,
                                                        leadId: target.id
                                                    }
                                                }
                                            )
                                        }

                                    >
                                        <BusinessCenterIcon sx={{ color: "#388e3c" }} />
                                    </IconButton>
                                </Tooltip>

                                {/* Silme */}
                                <Tooltip title="Delete">
                                    <IconButton onClick={() => handleDelete(target.id)}>
                                        <DeleteIcon sx={{ color: "#B42318" }} fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </MDBox>
                        )
                    };
                })
            );
            setRows(result);
        };

        buildRows();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targets]);

    /* ---------------- delete ---------------- */
    const handleDelete = async (id) => {
        if (!await appConfirm("Are you sure you want to delete this target?")) return;
        try {
            await CrudService.deleteTarget(id);
            setTargets((p) => p.filter((t) => t.id !== id));
            setNotif({ open: true, text: "Target deleted successfully." });
            setTimeout(() => setNotif({ open: false, text: "" }), 4000);
        } catch (err) {
            console.error(err);
            setNotif({ open: true, text: "Failed to delete target." });
            setTimeout(() => setNotif({ open: false, text: "" }), 4000);
        }
    };


    const table = useMemo(
        () => ({
            columns: [
                {
                    Header: "Lead Company",
                    accessor: "leadCompany",
                },
                {
                    Header: "Representative",
                    accessor: "leadName",
                },
                {
                    Header: "Selected Strategies",
                    accessor: "selectedStrategies",
                },
                {
                    Header: "Total Score",
                    accessor: "totalScore",
                },
                {
                    Header: "Highest Scoring Strategy",
                    accessor: "highestStrategy",
                },
                {
                    Header: "Actions",
                    accessor: "actions",
                    disableSortBy: true,
                },
            ],
            rows,
        }),
        [rows]
    );


    /* ---------------- render ---------------- */
    return (
        <DashboardLayout>
            <DashboardNavbar />
            <CrmPage>
                <CrmPageHeader
                    title="Strategic Customer List"
                    subtitle="Target companies matched to sales strategies, with scores and quick meeting / opportunity actions."
                    actions={
                        <CrmActionButton icon={<Add />} onClick={() => navigate("/target/create-lead/details")}>
                            Create Target
                        </CrmActionButton>
                    }
                />
                {notif.open && <CrmNotice tone="blue">{notif.text}</CrmNotice>}

                <CrmTableCard title="Target Companies" summary={loading ? "Loading…" : `${rows.length} records`}>
                    {loading ? (
                        <MDBox display="flex" justifyContent="center" py={6}>
                            <CircularProgress />
                        </MDBox>
                    ) : (
                        <DataTable table={table} canSearch />
                    )}
                </CrmTableCard>
            </CrmPage>
            <Footer />
        </DashboardLayout>
    );
}

export default TargetTable;
